/* eslint-disable react/display-name */
import { forwardRef } from 'react';

import { AspectRatio, type AspectRatioProps } from '@chakra-ui/react';
import Webcam, { type WebcamProps } from 'react-webcam';

import { getSecurityTokens } from '@/features/auth/authUtils';
import { logErrorToDataLayer } from '@/utils/errors';

type KnownWebcamPropsKeys = 'videoConstraints' | 'audioConstraints';

type KnownWebcamProps = Pick<Partial<WebcamProps>, KnownWebcamPropsKeys>;

type VideoRecordProps = AspectRatioProps &
  KnownWebcamProps & {
    webcamProps?: Omit<Partial<WebcamProps>, KnownWebcamPropsKeys>;
  };

export const VideoRecorder = forwardRef<Webcam, VideoRecordProps>(
  (props, ref) => {
    const { audioConstraints, videoConstraints, webcamProps, ...rest } = props;
    const securityTokens = getSecurityTokens();

    return (
      <AspectRatio
        ratio={3 / 2}
        pos="relative"
        overflow="hidden"
        bg="gray.700"
        rounded="xl"
        w="full"
        {...rest}
      >
        <Webcam
          ref={ref}
          audio
          muted
          style={{
            width: '100%',
            position: 'absolute',
          }}
          mirrored
          {...webcamProps}
          videoConstraints={{
            aspectRatio: 3 / 2,
            facingMode: 'user',
            width: { min: 640, ideal: 640, max: 1280 },
            height: { min: 480, ideal: 480, max: 720 },
            frameRate: 24,
            ...(typeof videoConstraints === 'object' ? videoConstraints : {}),
          }}
          audioConstraints={{
            noiseSuppression: true,
            echoCancellation: true,
            ...(typeof audioConstraints === 'object' ? audioConstraints : {}),
          }}
          onUserMediaError={(e) =>
            logErrorToDataLayer(e, 'User Media Error', {
              userToken: securityTokens?.jwtToken,
            })
          }
          onError={(e) =>
            logErrorToDataLayer(e, 'Webcam Initialization Error', {
              userToken: securityTokens?.jwtToken,
            })
          }
        />
      </AspectRatio>
    );
  }
);
